// SearchPopup.js
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

const SearchPopup = ({
  isOpen,
  onClose,
  onSelect,
  fetchOptions,
  minChars = 1,
  placeholder = "Search...",
  currentValue = "",
  anchorRef, // Reference to the input field for positioning
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0, placement: 'below' });
  const popupRef = useRef(null);
  const inputRef = useRef(null);

  // Calculate popup position based on anchor element and available space
  const calculatePosition = () => {
    if (!anchorRef?.current) return;

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    const popupHeight = 400;
    const popupWidth = Math.min(500, viewportWidth - 40);

    const spaceBelow = viewportHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const placement = spaceBelow >= 240 || spaceBelow > spaceAbove ? "below" : "above";

    const top =
      placement === "below"
        ? Math.min(anchorRect.bottom + 6, viewportHeight - 120)
        : Math.max(8, anchorRect.top - popupHeight - 6);

    let left = anchorRect.left;
    const maxLeft = viewportWidth - popupWidth - 10;
    left = Math.max(10, Math.min(left, maxLeft));

    setPosition({
      top,
      left,
      width: anchorRect.width,
      placement,
      popupWidth,
    });
  };

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSearchTerm(currentValue || "");
      // Recalculate position when popup opens
      setTimeout(() => calculatePosition(), 0);
    }
  }, [isOpen, currentValue]);

  // Recalculate position on window resize or scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true); // Capture scroll events in all elements

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [isOpen, onClose]);

  const fetchRef = useRef(fetchOptions);
  fetchRef.current = fetchOptions;

  useEffect(() => {
    const searchOptions = async () => {
      if (searchTerm.length >= minChars) {
        setLoading(true);
        setSearchError("");
        try {
          const results = await fetchRef.current(searchTerm);
          setOptions(results || []);
          setSelectedIndex(0);
        } catch (error) {
          setOptions([]);
          setSearchError(
            error?.message || "Unable to search. Try a different term.",
          );
        } finally {
          setLoading(false);
        }
      } else {
        setOptions([]);
        setSearchError("");
      }
    };

    const timeoutId = setTimeout(searchOptions, 200);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, minChars]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleSelect = (option) => {
    onSelect(option);
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => 
        prev < options.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && options[selectedIndex]) {
      e.preventDefault();
      handleSelect(options[selectedIndex]);
    } else if (e.key === "Tab") {
      e.preventDefault();
      // Close on tab to maintain proper tab order
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.3)",
        zIndex: 9999,
      }}
      onClick={onClose} // Close when clicking the backdrop
    >
      <div
        ref={popupRef}
        style={{
          position: "fixed",
          top: `${position.top}px`,
          left: `${position.left}px`,
          width: `${position.popupWidth || position.width}px`,
          backgroundColor: "white",
          borderRadius: "8px",
          padding: "16px",
          maxHeight: "400px",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
          border: "1px solid #ccc",
          // Add a small arrow pointing to the input field
          ...(position.placement === 'above' && {
            transform: 'translateY(-5px)',
          }),
          ...(position.placement === 'below' && {
            transform: 'translateY(5px)',
          }),
        }}
        onClick={(e) => e.stopPropagation()} // Prevent backdrop click from closing when clicking inside
      >
        {/* Arrow indicator */}
        <div
          style={{
            position: "absolute",
            [position.placement === 'above' ? 'bottom' : 'top']: "-6px",
            left: "20px",
            width: "12px",
            height: "12px",
            backgroundColor: "white",
            borderLeft: "1px solid #ccc",
            borderTop: "1px solid #ccc",
            transform: position.placement === 'above' ? "rotate(225deg)" : "rotate(45deg)",
          }}
        />

        <div style={{ marginBottom: "12px" }}>
          <h3 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: "600" }}>
            Search and Select
          </h3>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              width: "100%",
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              fontSize: "14px",
            }}
          />
        </div>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            border: "1px solid #eee",
            borderRadius: "4px",
            minHeight: "100px",
          }}
        >
          {loading && (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              Searching...
            </div>
          )}

          {!loading && searchError && (
            <div style={{ padding: "20px", textAlign: "center", color: "#c62828" }}>
              {searchError}
            </div>
          )}

          {!loading && !searchError && options.length === 0 && searchTerm.length >= minChars && (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              No results found for "{searchTerm}"
            </div>
          )}

          {!loading && searchTerm.length < minChars && (
            <div style={{ padding: "20px", textAlign: "center", color: "#666" }}>
              Type at least {minChars} character(s) to search
            </div>
          )}

          {!loading &&
            options.map((option, index) => (
              <div
                key={index}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelect(option);
                }}
                style={{
                  padding: "8px 12px",
                  cursor: "pointer",
                  borderBottom: "1px solid #f0f0f0",
                  backgroundColor: index === selectedIndex ? "#e3f2fd" : "white",
                  fontSize: "13px",
                  transition: "background-color 0.1s ease",
                }}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {option.label}
              </div>
            ))}
        </div>

        <div style={{ marginTop: "12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button
            onClick={onClose}
            style={{
              padding: "6px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
              fontSize: "12px",
            }}
          >
            Cancel (Esc)
          </button>
          <div style={{ fontSize: "11px", color: "#666" }}>
            {options.length} result{options.length !== 1 ? 's' : ''}
            {loading && " • Searching..."}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SearchPopup;